// src/pages/OrderDetailPage.jsx - İPTAL İŞLEMİ KALDIRILDI, SADECE İADE VAR
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Package, Clock, Truck, CheckCircle, XCircle, Calendar, MapPin,
  CreditCard, User, Phone, Mail, Download, Printer, MessageSquare, Star, Check, X,
  AlertCircle, RotateCcw, Camera
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAllCampaigns } from '../services/discountService';
import { getAllProducts } from '../services/productService';
import { requestOrderReturn, markReturnShipped } from '../services/returnService';
import { addProductReview } from '../services/reviewService';

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productReviews, setProductReviews] = useState({});

  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  useEffect(() => {
    if (!orderId) {
      toast.error('Sipariş bulunamadı');
      navigate('/orders');
      return;
    }

    loadOrderDetail();
  }, [orderId]);

  const loadOrderDetail = async () => {
    try {
      setLoading(true);

      const [campaignsResult, productsResult] = await Promise.all([
        getAllCampaigns(),
        getAllProducts()
      ]);

      const orderRef = doc(db, 'Orders', orderId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        const rawData = orderSnap.data();

        const orderData = {
          id: orderSnap.id,
          ...rawData,
          createdAt: rawData.createdAt?.toDate?.() || new Date(rawData.createdAt),
          deliveredAt: rawData.deliveredAt?.toDate?.() || (rawData.deliveredAt ? new Date(rawData.deliveredAt) : null),
          cancelledAt: rawData.cancelledAt?.toDate?.() || (rawData.cancelledAt ? new Date(rawData.cancelledAt) : null),
          updatedAt: rawData.updatedAt?.toDate?.() || (rawData.updatedAt ? new Date(rawData.updatedAt) : null)
        };

        console.log('📦 Sipariş yüklendi:', {
          id: orderData.id,
          orderNumber: orderData.orderNumber,
          status: orderData.status,
          createdAt: orderData.createdAt,
          deliveredAt: orderData.deliveredAt,
          returnStatus: orderData.returnStatus
        });

        setOrder(orderData);

        if (orderData.items) {
          const reviewedProducts = orderData.reviewedProducts || {};
          setProductReviews(reviewedProducts);
        }

        if (campaignsResult.success) {
          setCampaigns(campaignsResult.campaigns);
        }

        if (productsResult.success) {
          const activeProducts = productsResult.products.filter(product =>
            product.isActive !== false
          );
          setProducts(activeProducts);

          const categoriesSet = new Set();
          activeProducts.forEach(product => {
            if (product.category) {
              const mainCat = product.category.includes(' > ')
                ? product.category.split(' > ')[0]
                : product.category;
              categoriesSet.add(mainCat);
            }
          });
          setAvailableCategories(Array.from(categoriesSet));
        }
      } else {
        toast.error('Sipariş bulunamadı');
        navigate('/orders');
      }
    } catch (error) {
      console.error('Sipariş detayı yüklenirken hata:', error);
      toast.error('Sipariş detayı yüklenemedi');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: {
        label: 'Beklemede',
        icon: <Clock size={20} />,
        color: '#f59e0b',
        bgColor: '#fffbeb',
        description: 'Siparişiniz alındı ve işleme hazırlanıyor',
        step: 1
      },
      processing: {
        label: 'Hazırlanıyor',
        icon: <Package size={20} />,
        color: '#3b82f6',
        bgColor: '#eff6ff',
        description: 'Siparişiniz hazırlanıyor ve paketleniyor',
        step: 2
      },
      shipped: {
        label: 'Kargoda',
        icon: <Truck size={20} />,
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
        description: 'Siparişiniz kargoya verildi ve yolda',
        step: 3
      },
      delivered: {
        label: 'Teslim Edildi',
        icon: <CheckCircle size={20} />,
        color: '#22c55e',
        bgColor: '#f0fdf4',
        description: 'Siparişiniz başarıyla teslim edildi',
        step: 4
      },
      cancelled: {
        label: 'İptal Edildi',
        icon: <XCircle size={20} />,
        color: '#ef4444',
        bgColor: '#fef2f2',
        description: 'Sipariş iptal edildi',
        step: 0
      },
      returned: {
        label: 'İade Edildi',
        icon: <RotateCcw size={20} />,
        color: '#f59e0b',
        bgColor: '#fffbeb',
        description: 'Sipariş iade edildi',
        step: 0
      }
    };

    return statusMap[status] || statusMap.pending;
  };

  const formatDate = (date) => {
    if (!date) return '-';

    try {
      let dateObj;

      if (date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      }
      else if (date instanceof Date) {
        dateObj = date;
      }
      else if (typeof date === 'string') {
        dateObj = new Date(date);
      }
      else if (typeof date === 'number') {
        dateObj = new Date(date * 1000);
      }
      else if (date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      }
      else {
        console.warn('Bilinmeyen tarih formatı:', date);
        return '-';
      }

      if (isNaN(dateObj.getTime())) {
        console.warn('Geçersiz tarih:', date);
        return '-';
      }

      return dateObj.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Tarih formatlama hatası:', error, date);
      return '-';
    }
  };

  const handleMarkReturnShipped = async () => {
    if (!window.confirm('Ürünü kargoya verdiğinizi onaylıyor musunuz?')) {
      return;
    }

    try {
      setLoading(true);

      const result = await markReturnShipped(order.id);

      if (result.success) {
        toast.success(result.message);
        await loadOrderDetail(); // Sayfayı yenile
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // ✅ İADE EDİLEBİLİR Mİ KONTROL ET (SADECE TESLİM EDİLEN SİPARİŞLER, 7 GÜN İÇİNDE)
  const canReturn = () => {
    if (!order) return false;

    // İade talebi zaten varsa gösterme
    if (order.returnStatus) return false;

    // Sadece teslim edilmiş siparişler iade edilebilir
    if (order.status !== 'delivered') return false;

    // Teslim tarihinden itibaren 7 gün kontrolü
    if (order.deliveredAt) {
      try {
        let deliveryDate;

        if (order.deliveredAt.toDate && typeof order.deliveredAt.toDate === 'function') {
          deliveryDate = order.deliveredAt.toDate();
        } else if (order.deliveredAt instanceof Date) {
          deliveryDate = order.deliveredAt;
        } else if (typeof order.deliveredAt === 'string') {
          deliveryDate = new Date(order.deliveredAt);
        } else {
          return true;
        }

        const now = new Date();
        const daysPassed = Math.floor((now - deliveryDate) / (1000 * 60 * 60 * 24));
        return daysPassed <= 7;
      } catch (error) {
        console.error('Tarih parse hatası:', error);
        return true;
      }
    }

    return true;
  };

  // Değerlendirme yapılabilir mi kontrol et
  const canReview = (productId) => {
    if (!order) return false;
    if (order.status !== 'delivered') return false;
    return !productReviews[productId];
  };

  const handleReturnRequest = () => {
    setShowReturnModal(true);
  };

  const handleReviewClick = (item) => {
    setSelectedProduct(item);
    setShowReviewModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  if (loading) {
    return (
      <div className="order-detail-page">
        <Style />
        <div className="container">
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Sipariş detayı yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const statusInfo = getStatusInfo(order.status);

  return (
    <div className="order-detail-page">
      <Style />

      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
        availableCategories={availableCategories}
        products={products}
      />

      <div className="container">

        {/* Page Header */}
        <div className="page-header-order-detail">
          <div className="header-left-order-detail">
            <h1>
              <Package size={32} />
              Sipariş Detayı
            </h1>
            <p className="order-number">#{order.orderNumber}</p>
          </div>

          <div className="header-actions-order-detail">
            <button className="action-btn" onClick={handlePrint}>
              <Printer size={18} />
              Yazdır
            </button>
            <button className="action-btn">
              <Download size={18} />
              PDF İndir
            </button>
          </div>
        </div>

        {/* Sipariş Durum Takip Çubuğu */}
        {order.status !== 'cancelled' && order.status !== 'returned' && (
          <OrderProgressBar currentStatus={order.status} />
        )}

        {/* İade Bilgileri Kartı - Onaylandıysa göster */}
        {order.returnStatus === 'approved' && order.returnTrackingNumber && (
          <div className="detail-card return-cargo-card">
            <h3>
              <RotateCcw size={20} />
              İade Kargo Bilgileri
            </h3>

            <div className="info-box success">
              <CheckCircle size={20} />
              <div>
                <p><strong>İadeniz onaylandı!</strong></p>
                <p>Lütfen ürünü aşağıdaki kargo bilgileriyle gönderin.</p>
              </div>
            </div>

            <div className="cargo-info">
              <div className="cargo-row">
                <span className="label">Kargo Şirketi</span>
                <strong>Sürat Kargo</strong>
              </div>

              <div className="cargo-row">
                <span className="label">Takip Numarası</span>
                <div className="tracking-number">
                  {order.returnTrackingNumber}
                  <button
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(order.returnTrackingNumber);
                      toast.success('Takip numarası kopyalandı!');
                    }}
                  >
                    📋 Kopyala
                  </button>
                </div>
              </div>

              <div className="cargo-row">
                <span className="label">İade Adresi</span>
                <div className="address-text">
                  <strong>Trona Pazarlama</strong><br />
                  Manavgat Mahallesi, Örnek Sokak No:123<br />
                  Manavgat / Antalya - 07600<br />
                  Tel: 0555 123 45 67
                </div>
              </div>
            </div>

            {/* Kargoya Verdim Butonu */}
            {order.returnStatus === 'approved' && (
              <button
                className="btn-primary btn-ship-return"
                onClick={handleMarkReturnShipped}
              >
                <Truck size={18} />
                Kargoya Verdim
              </button>
            )}

            <a
              href={`https://gonderitakip.suratkargo.com.tr/Home/Track?code=${order.returnTrackingNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tracking-link"
            >
              🔍 Sürat Kargo'da Takip Et →
            </a>
          </div>
        )}

        {/* Status Section */}
        <div className="status-section">
          <div
            className="status-banner"
            style={{
              backgroundColor: statusInfo.bgColor,
              borderLeft: `6px solid ${statusInfo.color}`
            }}
          >
            <div
              className="status-icon"
              style={{ backgroundColor: statusInfo.color }}
            >
              {statusInfo.icon}
            </div>
            <div className="status-info">
              <h3 style={{ color: statusInfo.color }}>{statusInfo.label}</h3>
              <p>{statusInfo.description}</p>
            </div>

            {/* ✅ SADECE İADE BUTONU (İptal butonu kaldırıldı) */}
            <div className="action-buttons">
              {canReturn() && (
                <button className="return-btn" onClick={handleReturnRequest}>
                  <RotateCcw size={18} />
                  İade Talebi Oluştur
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="detail-grid">
          {/* Left Column */}
          <div className="detail-left">
            {/* Order Items */}
            <div className="detail-card">
              <h3>
                <Package size={20} />
                Sipariş İçeriği
              </h3>

              <div className="items-list">
                {order.items?.map((item, index) => (
                  <div key={index} className="item-row">
                    <img
                      src={item.productImage || '/placeholder.png'}
                      alt={item.productName}
                      onClick={() => navigate(`/product/${item.productId}`)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="item-details">
                      <h4
                        onClick={() => navigate(`/product/${item.productId}`)}
                        style={{ cursor: 'pointer', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.target.style.color = '#E5332A'}
                        onMouseLeave={(e) => e.target.style.color = '#2B2B2B'}
                      >
                        {item.productName}
                      </h4>
                      <div className="item-meta">
                        <span className="item-qty">Adet: {item.quantity}</span>
                        <span className="item-price">Birim Fiyat: {item.price.toFixed(2)}₺</span>
                      </div>

                      {/* Değerlendirme Butonu */}
                      {canReview(item.productId) && (
                        <button
                          className="review-btn"
                          onClick={() => handleReviewClick(item)}
                        >
                          <Star size={14} />
                          Değerlendir
                        </button>
                      )}

                      {productReviews[item.productId] && (
                        <div className="reviewed-badge">
                          <CheckCircle size={14} />
                          Değerlendirildi
                        </div>
                      )}
                    </div>
                    <div className="item-total">
                      {(item.price * item.quantity).toFixed(2)}₺
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Summary */}
              <div className="price-summary">
                <div className="price-row">
                  <span>Ara Toplam</span>
                  <span>{order.subtotal?.toFixed(2)}₺</span>
                </div>
                <div className="price-row">
                  <span>Kargo</span>
                  <span className={order.shippingCost === 0 ? 'free' : ''}>
                    {order.shippingCost === 0 ? 'Ücretsiz' : `${order.shippingCost.toFixed(2)}₺`}
                  </span>
                </div>
                <div className="price-row total">
                  <span>Toplam</span>
                  <strong>{order.total?.toFixed(2)}₺</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="detail-right">
            {/* Teslimat Adresi */}
            <div className="detail-card">
              <h3>
                <MapPin size={20} />
                Teslimat Adresi
              </h3>
              <div className="info-list">
                <div className="info-item">
                  <User size={18} />
                  <div>
                    <span className="label">Alıcı</span>
                    <p>{order.shippingAddress?.fullName || order.customerName}</p>
                  </div>
                </div>
                <div className="info-item">
                  <MapPin size={18} />
                  <div>
                    <span className="label">Adres</span>
                    <p>
                      {order.shippingAddress?.address || 'Adres bilgisi yok'}<br />
                      {order.shippingAddress?.district && `${order.shippingAddress.district}, `}
                      {order.shippingAddress?.city || ''}<br />
                      {order.shippingAddress?.postalCode && `Posta Kodu: ${order.shippingAddress.postalCode}`}
                    </p>
                  </div>
                </div>
                <div className="info-item">
                  <Phone size={18} />
                  <div>
                    <span className="label">Telefon</span>
                    <p>{order.shippingAddress?.phone || order.customerPhone || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ödeme Bilgileri */}
            <div className="detail-card">
              <h3>
                <CreditCard size={20} />
                Ödeme Bilgileri
              </h3>
              <div className="info-list">
                <div className="info-item">
                  <CreditCard size={18} />
                  <div>
                    <span className="label">Ödeme Yöntemi</span>
                    <p>
                      {order.paymentMethod === 'credit-card' ? 'Kredi Kartı' :
                        order.paymentMethod === 'debit-card' ? 'Banka Kartı' :
                          order.paymentMethod === 'cash-on-delivery' ? 'Kapıda Ödeme' :
                            'Diğer'}
                    </p>
                  </div>
                </div>
                <div className="info-item">
                  <CheckCircle size={18} />
                  <div>
                    <span className="label">Ödeme Durumu</span>
                    <p>
                      <span className={`payment-status ${order.paymentStatus || 'pending'}`}>
                        {order.paymentStatus === 'paid' ? 'Ödendi' :
                          order.paymentStatus === 'pending' ? 'Beklemede' :
                            'Beklemede'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sipariş Bilgileri */}
            <div className="detail-card">
              <h3>
                <Calendar size={20} />
                Sipariş Bilgileri
              </h3>
              <div className="info-list">
                <div className="info-item">
                  <Calendar size={18} />
                  <div>
                    <span className="label">Sipariş Tarihi</span>
                    <p>{formatDate(order.createdAt)}</p>
                  </div>
                </div>
                {order.deliveredAt && (
                  <div className="info-item">
                    <CheckCircle size={18} />
                    <div>
                      <span className="label">Teslim Tarihi</span>
                      <p>{formatDate(order.deliveredAt)}</p>
                    </div>
                  </div>
                )}
                <div className="info-item">
                  <Mail size={18} />
                  <div>
                    <span className="label">E-posta</span>
                    <p>{order.customerEmail}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Yardım */}
            <div className="help-card">
              <h4>
                <MessageSquare size={20} />
                Yardıma mı ihtiyacınız var?
              </h4>
              <p>Sipariş hakkında sorularınız için bizimle iletişime geçin.</p>
              <div className="help-buttons">
                <button className="help-btn">
                  <Phone size={18} />
                  Bizi Arayın
                </button>
                <button className="help-btn">
                  <MessageSquare size={18} />
                  Canlı Destek
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* İade Modal (Onaylı - Admin Onayı Gerekli) */}
      {showReturnModal && (
        <ReturnOrderModal
          order={order}
          onClose={() => setShowReturnModal(false)}
          onSuccess={loadOrderDetail}
        />
      )}

      {/* Değerlendirme Modal */}
      {showReviewModal && selectedProduct && (
        <ReviewModal
          order={order}
          product={selectedProduct}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedProduct(null);
          }}
          onSuccess={loadOrderDetail}
        />
      )}
    </div>
  );
}

// Helper fonksiyonlar
function getReturnStatusLabel(status) {
  const labels = {
    pending: 'Beklemede',
    approved: 'Onaylandı - Kargo Bekliyor',
    received: 'Ürün Teslim Alındı',
    completed: 'Tamamlandı - İade Yapıldı',
    rejected: 'Reddedildi'
  };
  return labels[status] || status;
}

// ==================== İADE MODAL (ONAYLI - ADMİN ONAYI GEREKLİ) ====================
function ReturnOrderModal({ order, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [hasDefect, setHasDefect] = useState(false);
  const [defectDescription, setDefectDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const returnReasons = [
    'Ürün hasarlı/kusurlu geldi',
    'Yanlış ürün geldi',
    'Ürün açıklamaya uygun değil',
    'Ürün beklentimi karşılamadı',
    'Geç teslim edildi',
    'Fikir değiştirdim',
    'Diğer'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      toast.error('Lütfen bir iade nedeni seçin');
      return;
    }

    if (hasDefect && !defectDescription.trim()) {
      toast.error('Lütfen kusur/hasar açıklaması yapın');
      return;
    }

    setLoading(true);

    try {
      const userId = localStorage.getItem('userId') || 'anonymous';

      const result = await requestOrderReturn(order.id, {
        reason,
        reasonDetail,
        hasDefect,
        defectDescription: hasDefect ? defectDescription : '',
        userId
      });

      if (result.success) {
        toast.success(result.message);
        onClose();
        onSuccess();
      } else {
        toast.error(result.error || 'İade talebi oluşturulamadı');
      }
    } catch (error) {
      console.error('İade talebi hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-order-detail">
          <h2>
            <RotateCcw size={24} />
            İade Talebi Oluştur
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="info-box warning">
              <AlertCircle size={20} />
              <div>
                <p><strong>BİLGİ:</strong> İade talebiniz incelenecek</p>
                <p className="small-text">Onay sonrası kargo bilgileri size iletilecektir.</p>
              </div>
            </div>

            <div className="info-box">
              <AlertCircle size={20} />
              <div>
                <p>Sipariş numarası: <strong>#{order.orderNumber}</strong></p>
                <p>Toplam tutar: <strong>{order.total?.toFixed(2)}₺</strong></p>
                {order.status === 'delivered' && (
                  <p className="small-text">Teslim tarihinden itibaren 7 gün içinde iade yapabilirsiniz.</p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>İade Nedeni *</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              >
                <option value="">Seçiniz...</option>
                {returnReasons.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Açıklama</label>
              <textarea
                value={reasonDetail}
                onChange={(e) => setReasonDetail(e.target.value)}
                placeholder="İade nedeninizi detaylı açıklayın..."
                rows={3}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={hasDefect}
                  onChange={(e) => setHasDefect(e.target.checked)}
                />
                <span>Üründe kusur/hasar var</span>
              </label>
            </div>

            {hasDefect && (
              <div className="form-group">
                <label>Kusur/Hasar Açıklaması *</label>
                <textarea
                  value={defectDescription}
                  onChange={(e) => setDefectDescription(e.target.value)}
                  placeholder="Lütfen kusur veya hasarı detaylı açıklayın..."
                  rows={3}
                  required
                />
              </div>
            )}

            <div className="warning-box">
              <AlertCircle size={18} />
              <div>
                <p><strong>İade Süreci:</strong></p>
                <ol>
                  <li>İade talebiniz satıcı tarafından incelenecek</li>
                  <li>Onaylandığında size kargo bilgileri gönderilecek</li>
                  <li>Ürünü belirlenen adrese göndereceksiniz</li>
                  <li>Ürün kontrolü sonrası ödeme iadeniz yapılacak</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Gönderiliyor...' : 'İade Talebi Gönder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== DEĞERLENDİRME MODAL (YENİ!) ====================
function ReviewModal({ order, product, onClose, onSuccess }) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error('En fazla 5 fotoğraf yükleyebilirsiniz');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Lütfen puan verin');
      return;
    }

    if (!comment.trim()) {
      toast.error('Lütfen yorum yazın');
      return;
    }

    setLoading(true);

    try {
      const userId = localStorage.getItem('userId') || 'anonymous';
      const userName = localStorage.getItem('userName') || 'Anonim';

      const result = await addProductReview({
        userId,
        userName,
        productId: product.productId,
        productName: product.productName,
        orderId: order.id,
        rating,
        title: title.trim(),
        comment: comment.trim(),
        images
      });

      if (result.success) {
        toast.success(result.message);
        onClose();
        onSuccess();
      } else {
        toast.error(result.error || 'Değerlendirme eklenemedi');
      }
    } catch (error) {
      console.error('Değerlendirme hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-review" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-order-detail">
          <h2>
            <Star size={24} />
            Ürünü Değerlendir
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Product Info */}
            <div className="review-product-info">
              <img src={product.productImage} alt={product.productName} />
              <div>
                <h4>{product.productName}</h4>
                <p className="verified-badge">
                  <CheckCircle size={14} />
                  Onaylanmış Alışveriş
                </p>
              </div>
            </div>

            {/* Rating Stars */}
            <div className="form-group">
              <label>Puanınız *</label>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="star-btn"
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    onClick={() => setRating(star)}
                  >
                    <Star
                      size={36}
                      fill={(hoveredRating || rating) >= star ? '#ffc107' : 'none'}
                      stroke={(hoveredRating || rating) >= star ? '#ffc107' : '#e0e0e0'}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="rating-text">
                  {rating === 5 ? 'Mükemmel!' :
                    rating === 4 ? 'Çok İyi' :
                      rating === 3 ? 'İyi' :
                        rating === 2 ? 'Orta' : 'Kötü'}
                </p>
              )}
            </div>

            {/* Title */}
            <div className="form-group">
              <label>Başlık (Opsiyonel)</label>
              <input
                type="text"
                className="form-input"
                style={{ height: '48px', fontSize: '15px', paddingLeft: '16px' }}
                placeholder="Örn: Harika bir ürün!"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
              />
            </div>

            {/* Comment */}
            <div className="form-group">
              <label>Yorumunuz *</label>
              <textarea
                className="form-input"
                placeholder="Ürün hakkındaki düşüncelerinizi paylaşın..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                required
                maxLength={1000}
              />
              <p className="char-count">{comment.length}/1000</p>
            </div>

            {/* Image Upload */}
            <div className="form-group">
              <label>Fotoğraflar (Opsiyonel - Max 5)</label>
              <div className="image-upload-area">
                {images.length < 5 && (
                  <label className="upload-btn">
                    <div><Camera size={40} /></div>
                    <div><span>Fotoğraf Ekle</span></div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}

                <div className="uploaded-images">
                  {images.map((img, index) => (
                    <div key={index} className="uploaded-image">
                      <img src={img} alt={`Upload ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => removeImage(index)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="info-box">
              <AlertCircle size={18} />
              <p className="small-text">
                Değerlendirmeniz moderasyon sonrası yayınlanacaktır. Lütfen saygılı bir dil kullanın.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || rating === 0}
            >
              {loading ? 'Gönderiliyor...' : 'Değerlendirmeyi Gönder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Sipariş Durum Takip Çubuğu Komponenti
function OrderProgressBar({ currentStatus }) {
  const steps = [
    { status: 'pending', label: 'Sipariş Alındı', icon: <Clock size={20} /> },
    { status: 'processing', label: 'Hazırlanıyor', icon: <Package size={20} /> },
    { status: 'shipped', label: 'Kargoda', icon: <Truck size={20} /> },
    { status: 'delivered', label: 'Teslim Edildi', icon: <CheckCircle size={20} /> }
  ];

  // Mobil için kare düzen sıralaması
  const mobileSteps = [
    { status: 'pending', label: 'Sipariş Alındı', icon: <Clock size={20} /> },      // [1] Sol üst
    { status: 'processing', label: 'Hazırlanıyor', icon: <Package size={20} /> },   // [2] Sağ üst
    { status: 'delivered', label: 'Teslim Edildi', icon: <CheckCircle size={20} /> }, // [3] Sol alt
    { status: 'shipped', label: 'Kargoda', icon: <Truck size={20} /> }              // [4] Sağ alt
  ];

  const statusOrder = ['pending', 'processing', 'shipped', 'delivered'];
  const currentStepIndex = statusOrder.indexOf(currentStatus);

  // Mobil ekran kontrolü
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displaySteps = isMobile ? mobileSteps : steps;

  return (
    <div className="order-progress-wrapper">
      <div className="order-progress-bar">
        {displaySteps.map((step, index) => {
          const actualStepIndex = statusOrder.indexOf(step.status);
          const isCompleted = actualStepIndex < currentStepIndex;
          const isActive = actualStepIndex === currentStepIndex;
          const isPending = actualStepIndex > currentStepIndex;

          return (
            <React.Fragment key={step.status}>
              <div className={`progress-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isPending ? 'pending' : ''}`}>
                <div className="progress-step-icon">
                  {isCompleted ? <Check size={20} /> : step.icon}
                </div>
                <div className="progress-step-label">{step.label}</div>
              </div>

              {/* Desktop: Normal çizgiler */}
              {!isMobile && index < steps.length - 1 && (
                <div className={`progress-line ${isCompleted ? 'completed' : ''}`}></div>
              )}
            </React.Fragment>
          );
        })}

        {/* Mobil: Kare düzen bağlantı çizgileri */}
        {isMobile && (
          <>
            {/* Sipariş Alındı → Hazırlanıyor (yatay, sağa) */}
            <div className={`progress-line-mobile horizontal top ${statusOrder.indexOf('processing') <= currentStepIndex ? 'completed' : ''
              }`}></div>

            {/* Hazırlanıyor → Kargoda (dikey, aşağı) */}
            <div className={`progress-line-mobile vertical right ${statusOrder.indexOf('shipped') <= currentStepIndex ? 'completed' : ''
              }`}></div>

            {/* Kargoda → Teslim Edildi (yatay, sola) */}
            <div className={`progress-line-mobile horizontal bottom ${statusOrder.indexOf('delivered') <= currentStepIndex ? 'completed' : ''
              }`}></div>

            {/* Sipariş Alındı → Teslim Edildi (dikey, aşağı) */}
            <div className={`progress-line-mobile vertical left ${statusOrder.indexOf('delivered') <= currentStepIndex ? 'completed' : ''
              }`}></div>
          </>
        )}
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        background: #f8f9fa;
      }

      .order-detail-page {
        min-height: 100vh;
        padding: 0 0 0px;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      /* YENİ: Değerlendirme Butonları */
      .review-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background: linear-gradient(135deg, #ffc107, #ff9800);
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        margin-top: 8px;
        transition: all 0.2s;
      }
      
      .review-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(255, 193, 7, 0.4);
      }
      
      .reviewed-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background: rgba(34, 197, 94, 0.1);
        color: #22c55e;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        margin-top: 8px;
      }
      
      /* YENİ: Review Modal Styles */
      .modal-review {
        max-width: 700px;
      }
      
      .review-product-info {
        display: flex;
        gap: 16px;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 12px;
        margin-bottom: 24px;
      }
      
      .review-product-info img {
        width: 80px;
        height: 80px;
        object-fit: contain;
        background: #fff;
        border-radius: 8px;
        padding: 8px;
      }
      
      .review-product-info h4 {
        font-size: 16px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }
      
      .verified-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #22c55e;
        font-weight: 600;
      }
      
      .rating-stars {
        display: flex;
        gap: 8px;
        margin: 12px 0;
      }
      
      .star-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        transition: transform 0.2s;
      }
      
      .star-btn:hover {
        transform: scale(1.1);
      }
      
      .rating-text {
        font-size: 18px;
        font-weight: 600;
        color: #ffc107;
        margin-top: 8px;
      }
      
      .char-count {
        font-size: 12px;
        color: #868e96;
        text-align: right;
        margin-top: 4px;
      }
      
      .image-upload-area {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 8px;
      }
      
      .upload-btn {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 150px;
        height: 150px;
        border: 3px dashed #ddd;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        color: #868e96;
        background: #fafafa;
        padding: 20px;
      }
      
      .upload-btn svg {
        width: 40px;
        height: 40px;
        color: #868e96;
        transition: all 0.3s ease;
      }
      
      .upload-btn span {
        font-size: 14px;
        font-weight: 600;
        color: #495057;
        text-align: center;
        line-height: 1.4;
        transition: all 0.3s ease;
      }
      
      .upload-btn:hover {
        border-color: #E5332A;
        background: #fff5f5;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(229, 51, 42, 0.15);
      }
      
      .upload-btn:hover svg {
        color: #E5332A;
        transform: scale(1.1);
      }
      
      .upload-btn:hover span {
        color: #E5332A;
      }
      
      .uploaded-images {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      
      .uploaded-image {
        position: relative;
        width: 150px;
        height: 150px;
        border-radius: 16px;
        overflow: hidden;
        border: 3px solid #e9ecef;
        transition: all 0.3s ease;
      }
      
      .uploaded-image:hover {
        border-color: #E5332A;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      
      .uploaded-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      .remove-image {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(229, 51, 42, 0.95);
        color: #fff;
        border: 2px solid white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }
      
      .remove-image:hover {
        background: #E5332A;
        transform: scale(1.15);
        box-shadow: 0 4px 12px rgba(229, 51, 42, 0.4);
      }

      /* İade Kargo Kartı */
.return-cargo-card {
  border: 3px solid #22c55e !important;
  background: linear-gradient(to bottom, #f0fdf4, #fff);
}

.cargo-info {
  margin: 20px 0;
}

.cargo-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #e9ecef;
}

.cargo-row .label {
  font-size: 14px;
  color: #868e96;
  font-weight: 600;
}

.tracking-number {
  font-size: 22px;
  font-weight: 700;
  color: #22c55e;
  font-family: monospace;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.copy-btn {
  padding: 6px 12px;
  background: #22c55e;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.copy-btn:hover {
  background: #16a34a;
  transform: translateY(-1px);
}

.btn-ship-return {
  width: 100%;
  margin-top: 20px;
  background: #22c55e !important;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-ship-return:hover {
  background: #16a34a !important;
}

.tracking-link {
  display: block;
  text-align: center;
  margin-top: 16px;
  color: #22c55e;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
}

.tracking-link:hover {
  color: #16a34a;
  transform: translateX(4px);
}

.address-text {
  text-align: right;
  line-height: 1.6;
  color: #495057;
}

      /* =============== PAGE CONTENT =============== */

      /* Loading */
      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 20px;
      }

      .spinner-large {
        width: 60px;
        height: 60px;
        border: 4px solid #e9ecef;
        border-top-color: #E5332A;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      /* Page Header */
      .page-header-order-detail {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-top: 32px;
        margin-bottom: 32px;
        padding: 32px;
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        border-radius: 20px;
        color: #fff;
      }

      .header-left-order-detail h1 {
        display: flex;
        align-items: center;
        gap: 16px;
        font-size: 32px;
        margin-bottom: 8px;
      }

      .order-number {
        font-size: 18px;
        opacity: 0.8;
        font-weight: 600;
      }

      .page-header-order-detail .header-actions-order-detail {
        display: flex;
        gap: 12px;
      }

      .action-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        color: #fff;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .action-btn:hover {
        background: rgba(255, 255, 255, 0.25);
      }

      /* ============ PROGRESS BAR STYLES - MOBİL UYUMLU ============ */
      .order-progress-wrapper {
        margin: 32px 0;
        background: #fff;
        border-radius: 20px;
        padding: 40px;
        border: 2px solid #e9ecef;
      }

      .order-progress-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        position: relative;
      }

      .progress-step {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        flex: 1;
        position: relative;
        z-index: 2;
      }

      .progress-step-icon {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #e9ecef;
        color: #868e96;
        transition: all 0.3s ease;
        border: 4px solid #fff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .progress-step.active .progress-step-icon {
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        color: #fff;
        box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
        animation: pulse 2s ease-in-out infinite;
      }

      .progress-step.completed .progress-step-icon {
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        color: #fff;
        box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
      }

      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }

      .progress-step-label {
        font-size: 14px;
        font-weight: 600;
        color: #868e96;
        text-align: center;
        transition: all 0.3s ease;
        max-width: 100px;
        line-height: 1.3;
      }

      .progress-step.active .progress-step-label {
        color: #3b82f6;
        font-size: 15px;
      }

      .progress-step.completed .progress-step-label {
        color: #22c55e;
      }

      .progress-line {
        flex: 1;
        height: 4px;
        background: #e9ecef;
        position: relative;
        margin: 0 -20px;
        z-index: 1;
        transition: all 0.5s ease;
      }

      .progress-line.completed {
        background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
      }

      /* İptal/İade Talep Durumu Banner */
      .request-status-banner {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 20px 24px;
        border-radius: 16px;
        margin-bottom: 24px;
        border: 2px solid;
      }

      .request-status-banner.pending {
        background: #fffbeb;
        border-color: #f59e0b;
        color: #92400e;
      }

      .request-status-banner.approved {
        background: #f0fdf4;
        border-color: #22c55e;
        color: #166534;
      }

      .request-status-banner.rejected {
        background: #fef2f2;
        border-color: #ef4444;
        color: #991b1b;
      }

      .request-status-banner h4 {
        font-size: 16px;
        margin-bottom: 4px;
        font-weight: 700;
      }

      .request-status-banner p {
        font-size: 14px;
        opacity: 0.9;
      }

      /* Status Section */
      .status-section {
        margin-bottom: 32px;
      }

      .status-banner {
        display: flex;
        gap: 20px;
        padding: 24px;
        border-radius: 16px;
        margin-bottom: 24px;
        position: relative;
      }

      .status-icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        flex-shrink: 0;
      }

      .status-info {
        flex: 1;
      }

      .status-info h3 {
        font-size: 24px;
        margin-bottom: 8px;
      }

      .status-info p {
        font-size: 16px;
        opacity: 0.8;
      }

      /* Action Buttons (İptal/İade) */
      .action-buttons {
        display: flex;
        gap: 12px;
        margin-left: auto;
      }

      .cancel-btn,
      .return-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        border: none;
        border-radius: 12px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }

      .cancel-btn {
        background: #fff;
        color: #ef4444;
        border: 2px solid #ef4444;
      }

      .cancel-btn:hover {
        background: #fef2f2;
      }

      .return-btn {
        background: #fff;
        color: #f59e0b;
        border: 2px solid #f59e0b;
      }

      .return-btn:hover {
        background: #fffbeb;
      }

      /* Detail Grid */
      .detail-grid {
        display: grid;
        grid-template-columns: 1fr 400px;
        gap: 24px;
        align-items: start;
      }

      .detail-card {
        background: #fff;
        border-radius: 20px;
        padding: 28px;
        border: 2px solid #e9ecef;
        margin-bottom: 24px;
      }

      .detail-card h3 {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 20px;
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 2px solid #f8f9fa;
        color: #2B2B2B;
      }

      /* Items List */
      .items-list {
        margin-bottom: 24px;
      }

      .item-row {
        display: flex;
        gap: 16px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        margin-bottom: 12px;
      }

      .item-row img {
        width: 80px;
        height: 80px;
        object-fit: contain;
        background: #fff;
        border-radius: 10px;
        padding: 8px;
        transition: all 0.3s ease;
      }
      
      .item-row img:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(229, 51, 42, 0.3);
      }

      .item-details {
        flex: 1;
      }

      .item-details h4 {
        font-size: 16px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .item-meta {
        display: flex;
        gap: 16px;
        font-size: 14px;
        color: #868e96;
      }

      .item-total {
        font-size: 20px;
        font-weight: 700;
        color: #E5332A;
      }

      /* Price Summary */
      .price-summary {
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .price-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;
        font-size: 16px;
      }

      .price-row.total {
        font-size: 20px;
        padding-top: 12px;
        border-top: 2px solid #e9ecef;
        margin-top: 12px;
        margin-bottom: 0;
      }

      .price-row.total strong {
        color: #E5332A;
      }

      .price-row .free {
        color: #22c55e;
        font-weight: 600;
      }

      /* Info List */
      .info-list {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .info-item {
        display: flex;
        gap: 12px;
      }

      .info-item > svg {
        color: #E5332A;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .info-item .label {
        display: block;
        font-size: 13px;
        color: #868e96;
        margin-bottom: 4px;
        font-weight: 600;
      }

      .info-item p {
        font-size: 15px;
        color: #2B2B2B;
        line-height: 1.6;
      }

      .payment-status {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 6px;
        font-weight: 600;
        font-size: 14px;
      }

      .payment-status.paid {
        background: #dcfce7;
        color: #16a34a;
      }

      .payment-status.pending {
        background: #fef3c7;
        color: #ca8a04;
      }

      /* Help Card */
      .help-card {
        background: linear-gradient(135deg, #E5332A 0%, #c72a22 100%);
        padding: 24px;
        border-radius: 16px;
        color: #fff;
        margin-bottom: 32px;
      }

      .help-card h4 {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 18px;
        margin-bottom: 8px;
      }

      .help-card p {
        font-size: 14px;
        opacity: 0.9;
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .help-buttons {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .help-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px;
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 10px;
        color: #fff;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .help-btn:hover {
        background: rgba(255, 255, 255, 0.25);
      }

      /* =============== MODAL STYLES =============== */
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 20px;
        animation: fadeIn 0.2s ease;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .modal-content {
        background: #fff;
        border-radius: 24px;
        max-width: 600px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        animation: slideUp 0.3s ease;
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .modal-header-order-detail {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 28px;
        border-bottom: 2px solid #f8f9fa;
      }

      .modal-header-order-detail h2 {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 22px;
        color: #2B2B2B;
      }

      .modal-close {
        background: none;
        border: none;
        color: #868e96;
        cursor: pointer;
        padding: 8px;
        border-radius: 8px;
        transition: all 0.2s;
      }

      .modal-close:hover {
        background: #f8f9fa;
        color: #2B2B2B;
      }

      .modal-body {
        padding: 28px;
      }

      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 20px 28px;
        border-top: 2px solid #f8f9fa;
      }

      /* Form Styles */
      .form-group {
        margin-bottom: 24px;
      }

      .form-group label {
        display: block;
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .form-group select,
      .form-group textarea,
      .form-input {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        font-size: 15px;
        font-family: inherit;
        transition: all 0.2s;
      }

      .form-group select:focus,
      .form-group textarea:focus,
      .form-input:focus {
        outline: none;
        border-color: #E5332A;
        background: #fff;
      }

      .form-group textarea {
        resize: vertical;
        min-height: 80px;
      }

      .checkbox-group {
        margin-bottom: 20px;
      }

      .checkbox-group label {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        font-weight: 500;
      }

      .checkbox-group input[type="checkbox"] {
        width: 20px;
        height: 20px;
        cursor: pointer;
      }

      /* Info & Warning Boxes */
      .info-box,
      .warning-box {
        display: flex;
        gap: 12px;
        padding: 16px;
        border-radius: 12px;
        margin-bottom: 20px;
      }

      .info-box {
        background: #eff6ff;
        border: 2px solid #3b82f6;
        color: #1e40af;
      }

      .warning-box {
        background: #fffbeb;
        border: 2px solid #f59e0b;
        color: #92400e;
      }

      .info-box.warning {
        background: #fffbeb;
        border: 2px solid #f59e0b;
        color: #92400e;
      }

      .info-box svg,
      .warning-box svg {
        flex-shrink: 0;
        margin-top: 2px;
      }

      .info-box p,
      .warning-box p {
        margin: 0;
        font-size: 14px;
        line-height: 1.6;
      }

      .info-box strong,
      .warning-box strong {
        display: block;
        margin-bottom: 8px;
      }

      .warning-box ol,
      .warning-box ul {
        margin-top: 8px;
        padding-left: 20px;
      }

      .warning-box li {
        margin-bottom: 4px;
      }

      .small-text {
        font-size: 13px !important;
        opacity: 0.8;
      }

      /* Buttons */
      .btn-primary,
      .btn-secondary,
      .btn-danger {
        padding: 12px 24px;
        border: none;
        border-radius: 12px;
        font-weight: 600;
        font-size: 15px;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-primary {
        background: #E5332A;
        color: #fff;
      }

      .btn-primary:hover:not(:disabled) {
        background: #c72a22;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(229, 51, 42, 0.3);
      }

      .btn-secondary {
        background: #f8f9fa;
        color: #495057;
      }

      .btn-secondary:hover:not(:disabled) {
        background: #e9ecef;
      }

      .btn-danger {
        background: #ef4444;
        color: #fff;
      }

      .btn-danger:hover:not(:disabled) {
        background: #dc2626;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
      }

      .btn-primary:disabled,
      .btn-secondary:disabled,
      .btn-danger:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* Print Styles */
      @media print {
        .back-btn,
        .page-header-order-detail .header-actions-order-detail,
        .help-card,
        .order-progress-wrapper,
        .cancel-btn,
        .return-btn,
        .modal-overlay,
        .header-order-detail,
        .review-btn,
        .reviewed-badge {
          display: none !important;
        }

        .detail-grid {
          grid-template-columns: 1fr;
        }
      }

      /* ============ RESPONSIVE ============ */
      @media (max-width: 992px) {
        .detail-grid {
          grid-template-columns: 1fr;
        }

        .page-header-order-detail {
          flex-direction: column;
          gap: 20px;
        }

        .page-header-order-detail .header-actions-order-detail {
          width: 100%;
        }

        .action-btn {
          flex: 1;
        }

        .contact-info {
          display: none;
        }

        /* Progress bar tablet optimizasyonu */
        .order-progress-wrapper {
          padding: 32px 24px;
        }

        .progress-step-icon {
          width: 56px;
          height: 56px;
        }

        .progress-step-label {
          font-size: 13px;
          max-width: 90px;
        }

        .action-buttons {
          flex-direction: column;
          margin-left: 0;
          margin-top: 16px;
          width: 100%;
        }

        .cancel-btn,
        .return-btn {
          width: 100%;
          justify-content: center;
        }
      }

      /* ============ MOBİL RESPONSIVE - PROGRESS BAR ÖZELLEŞTİRME ============ */
      @media (max-width: 768px) {
        .container {
          padding: 0 16px;
        }

        .order-detail-page {
          padding: 0 0 60px;
        }

        .page-header-order-detail {
          padding: 20px;
          border-radius: 16px;
          margin-top: 20px;
          margin-bottom: 24px;
        }

        .header-left-order-detail h1 {
          font-size: 22px;
          gap: 12px;
        }

        .order-number {
          font-size: 14px;
        }

        .status-banner {
          flex-direction: column;
        }

        .item-row {
          flex-direction: column;
          text-align: center;
          padding: 16px;
        }

        .item-row img {
          margin: 0 auto;
        }

        .header-content-order-detail {
          gap: 16px;
        }

        .header-order-detail .header-actions-order-detail {
          width: 100%;
          justify-content: space-between;
        }

        .top-links {
          display: none;
        }

        /* ============ MOBİL PROGRESS BAR - 2x2 GRID DÜZEN ============ */
        .order-progress-bar {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 48px 32px; /* ✅ ARTIRILDI: Önceden 24px 16px idi */
  }

  /* Progress Step Genel Ayarlar */
  .progress-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 2;
  }

  /* ✅ ÜST SATIR (Sipariş Alındı, Hazırlanıyor) - Label üstte */
  .progress-step:nth-child(1),
  .progress-step:nth-child(2) {
    flex-direction: column-reverse; /* İkon alta, label üste */
  }

  /* ✅ ALT SATIR (Teslim Edildi, Kargoda) - Label altta (normal) */
  .progress-step:nth-child(3),
  .progress-step:nth-child(4) {
    flex-direction: column; /* Label alta, ikon üste (varsayılan) */
  }

  /* Mobil Progress Çizgileri */
  .progress-line-mobile {
    position: absolute;
    background: #e9ecef;
    z-index: 0;
    transition: all 0.5s ease;
  }

  /* Üst yatay çizgi: Sipariş Alındı → Hazırlanıyor */
  .progress-line-mobile.horizontal.top {
    height: 3px;
    top: calc(26px + 24px); /* ✅ AYARLANDI: Label yüksekliği eklendi */
    left: calc(25% + 26px);
    width: calc(50% - 52px);
  }

  /* Sağ dikey çizgi: Hazırlanıyor → Kargoda */
  .progress-line-mobile.vertical.right {
    width: 3px;
    left: 75%;
    top: calc(26px + 26px + 24px); /* ✅ AYARLANDI: Label yüksekliği + gap */
    height: calc(50% - 52px + 48px - 24px); /* ✅ AYARLANDI: Yeni gap değeri */
  }

  /* Alt yatay çizgi: Kargoda → Teslim Edildi (sola doğru) */
  .progress-line-mobile.horizontal.bottom {
    height: 3px;
    top: calc(50% + 26px + 24px); /* ✅ AYARLANDI */
    left: calc(25% + 26px);
    width: calc(50% - 52px);
  }

  /* Sol dikey çizgi: Sipariş Alındı → Teslim Edildi */
  .progress-line-mobile.vertical.left {
    width: 3px;
    left: 25%;
    top: calc(26px + 26px + 24px); /* ✅ AYARLANDI */
    height: calc(50% - 52px + 48px - 24px); /* ✅ AYARLANDI */
  }

  /* Tamamlanmış çizgiler - Yeşil gradient */
  .progress-line-mobile.completed {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  }

  /* Yatay çizgiler için gradient yönü */
  .progress-line-mobile.horizontal.completed {
    background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
  }

  /* Dikey çizgiler için gradient yönü */
  .progress-line-mobile.vertical.completed {
    background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
  }

  /* Label stilleri - okunabilirlik için */
  .progress-step-label {
    font-size: 12px;
    font-weight: 600;
    color: #868e96;
    text-align: center;
    max-width: 100%;
    line-height: 1.3;
  }

  .progress-step.active .progress-step-label {
    color: #3b82f6;
    font-size: 13px;
  }

  .progress-step.completed .progress-step-label {
    color: #22c55e;
  }

        /* Detail cards */
        .detail-card {
          padding: 20px;
          border-radius: 16px;
        }

        .detail-card h3 {
          font-size: 18px;
          margin-bottom: 20px;
        }

        /* Modals */
        .modal-content {
          max-height: 85vh;
          border-radius: 20px;
        }

        .modal-header-order-detail {
          padding: 20px;
        }

        .modal-header-order-detail h2 {
          font-size: 18px;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          padding: 16px 20px;
          flex-direction: column;
        }

        .btn-primary,
        .btn-secondary,
        .btn-danger {
          width: 100%;
        }

        /* Review elements */
        .review-product-info {
          flex-direction: column;
          text-align: center;
        }

        .review-product-info img {
          margin: 0 auto;
        }

        .upload-btn,
        .uploaded-image {
          width: 120px;
          height: 120px;
        }

        .upload-btn svg {
          width: 32px;
          height: 32px;
        }

        .upload-btn span {
          font-size: 12px;
        }
      }

      /* ============ KÜÇÜK MOBİL (480px ve altı) ============ */
      @media (max-width: 480px) {
        .container {
          padding: 0 12px;
        }

        .order-progress-bar {
    gap: 60px 44px; /* ✅ KÜÇÜK MOBİL İÇİN AYARLANDI */
  }

  .progress-line-mobile.horizontal.top {
    height: 3px;
    top: calc(24px + 32px); /* ✅ AYARLANDI: Daha küçük ikon + label */
    left: calc(25% + 12px);
    width: calc(60% - 16px);
  }

  .progress-line-mobile.vertical.right {
    width: 3px;
    left: 78%;
    top: calc(24px + 24px + 20px); /* ✅ AYARLANDI */
    height: calc(50% - 8px + 40px - 20px); /* ✅ AYARLANDI */
  }

  .progress-line-mobile.horizontal.bottom {
    height: 3px;
    top: calc(50% + 24px + 32px); /* ✅ AYARLANDI */
    left: calc(25% + 12px);
    width: calc(60% - 16px);
  }

  .progress-line-mobile.vertical.left {
    width: 3px;
    left: 22%;
    top: calc(24px + 24px + 20px); /* ✅ AYARLANDI */
    height: calc(50% - 48px + 40px - 20px); /* ✅ AYARLANDI */
  }

  .progress-step-label {
    font-size: 11px;
  }

  .progress-step.active .progress-step-label {
    font-size: 12px;
  }

        .page-header-order-detail {
          padding: 16px;
        }

        .header-left-order-detail h1 {
          font-size: 20px;
        }

        .detail-card {
          padding: 16px;
        }

        .item-row {
          padding: 12px;
        }

        .upload-btn,
        .uploaded-image {
          width: 100px;
          height: 100px;
        }
      }
    `}</style>
  );
}